import { z } from "zod";
import type { ERPProviderKey } from "./constants";
import { ERPProviderKeySchema } from "./schemas";
import type { ItemUnit } from "../schemas/items";

/** A setting an ERP can lock, with the value it forces. */
export type EnforcedSettings = Partial<{
  requireLocation: true;
  requireShipTo: true;
  requireLengthAndWidth: true;
  requireLineItem: true;
  maxQuantityBreaksPerLine: number;
  allowFractionalQuantities: false;
  maxQuoteNumberLength: number;
  maxDeliveryTermsLength: number;
  allowDiscountLines: false;
  allowDuplicateItems: false;
  allowShipToCreation: false;
  maxPartNumberLength: number;
  maxPurchaseCostBreaks: number;
  allowOperationEfficiency: false;
  allowOperationQuantityBreaks: false;
  allowOperationQtyPer: false;
  allowComponentFixedQuantity: false;
  allowComponentScrap: false;
  allowComponentCostOverride: false;
  allowComponentCostingMethodOverride: false;
  allowComponentOperationLinks: false;
  allowedItemUnits: ItemUnit[];
}>;

/** Name of a setting an ERP can lock. */
export type EnforcedSettingKey = keyof EnforcedSettings;

/** Quote settings an ERP can lock. */
export const QUOTE_ENFORCED_SETTING_KEYS = [
  "requireLocation",
  "requireShipTo",
  "requireLengthAndWidth",
  "requireLineItem",
  "maxQuantityBreaksPerLine",
  "allowFractionalQuantities",
  "maxQuoteNumberLength",
  "maxDeliveryTermsLength",
  "allowDiscountLines",
  "allowDuplicateItems",
  "allowShipToCreation",
] as const satisfies readonly EnforcedSettingKey[];

/** Item settings an ERP can lock. */
export const ITEM_ENFORCED_SETTING_KEYS = [
  "maxPartNumberLength",
  "maxPurchaseCostBreaks",
  "allowOperationEfficiency",
  "allowOperationQuantityBreaks",
  "allowOperationQtyPer",
  "allowComponentFixedQuantity",
  "allowComponentScrap",
  "allowComponentCostOverride",
  "allowComponentCostingMethodOverride",
  "allowComponentOperationLinks",
  "allowedItemUnits",
] as const satisfies readonly EnforcedSettingKey[];

/** Every setting an ERP can lock. */
export const ENFORCED_SETTING_KEYS = [
  ...QUOTE_ENFORCED_SETTING_KEYS,
  ...ITEM_ENFORCED_SETTING_KEYS,
] as const satisfies readonly EnforcedSettingKey[];

const NUMERIC_ENFORCED_SETTING_KEYS = [
  "maxQuantityBreaksPerLine",
  "maxQuoteNumberLength",
  "maxDeliveryTermsLength",
  "maxPartNumberLength",
  "maxPurchaseCostBreaks",
] as const satisfies readonly EnforcedSettingKey[];

/** Which ERPs lock each setting, keyed by setting name. */
export const SettingsLockedBySchema = z.partialRecord(
  z.enum(ENFORCED_SETTING_KEYS),
  z.array(ERPProviderKeySchema),
);
export type SettingsLockedBy = z.infer<typeof SettingsLockedBySchema>;

/** Merged ERP locks: the forced values and which ERPs force each one. */
export type SettingsLocks = {
  values: EnforcedSettings;
  lockedBy: SettingsLockedBy;
};

/** Display names for ERP providers, used for "Required by" labels. */
export const ERP_PROVIDER_LABELS: Record<ERPProviderKey, string> = {
  dynamics_business_central: "Dynamics 365 Business Central",
  hubspot: "HubSpot",
  salesforce: "Salesforce",
  epicor: "Epicor",
  prophet21: "Prophet 21",
  jobboss2: "JobBOSS²",
  odyssey: "Odyssey",
  eniteo: "Eniteo",
  fake_erp: "Fake ERP",
};

/** Per-provider lock declaration. */
export const ERP_ENFORCED_SETTINGS: Partial<
  Record<ERPProviderKey, EnforcedSettings>
> = {
  prophet21: {
    requireLocation: true,
    requireShipTo: true,
    maxQuantityBreaksPerLine: 1,
    maxQuoteNumberLength: 36,
    allowDiscountLines: false,
    allowDuplicateItems: false,
  },
  epicor: {
    maxPartNumberLength: 50,
    maxQuoteNumberLength: 8,
    allowDiscountLines: false,
    allowOperationEfficiency: false,
    allowComponentCostOverride: false,
  },
  jobboss2: {
    maxQuantityBreaksPerLine: 8,
    allowFractionalQuantities: false,
    maxQuoteNumberLength: 26,
    maxDeliveryTermsLength: 40,
    maxPartNumberLength: 50,
    maxPurchaseCostBreaks: 8,
    allowOperationEfficiency: false,
    allowOperationQuantityBreaks: false,
    allowOperationQtyPer: false,
    allowComponentFixedQuantity: false,
    allowComponentScrap: false,
    allowComponentCostingMethodOverride: false,
    allowComponentOperationLinks: false,
    allowedItemUnits: ["each", "sets", "lots"],
  },
  eniteo: {
    requireLocation: true,
    requireLengthAndWidth: true,
    requireLineItem: true,
    maxQuantityBreaksPerLine: 1,
    maxQuoteNumberLength: 26,
    allowDiscountLines: false,
    allowShipToCreation: false,
  },
};

/**
 * Merges enabled providers' declarations, strictest wins.
 * @param entries - Each enabled provider with the settings it enforces
 * @returns The merged forced values and the providers locking each setting
 */
export function mergeEnforcedSettings(
  entries: { providerId: ERPProviderKey; settings: EnforcedSettings }[],
): SettingsLocks {
  return entries.reduce<SettingsLocks>(
    (merged, { providerId, settings }) => ({
      values: mergeValues(merged.values, settings),
      lockedBy: mergeLockedBy(merged.lockedBy, providerId, settings),
    }),
    { values: {}, lockedBy: {} },
  );
}

/**
 * Combines two sets of forced values: boolean locks always apply, numbers take the minimum, lists keep only shared entries.
 * @param current - The values merged so far
 * @param next - The next provider's forced values
 * @returns The combined forced values
 */
function mergeValues(
  current: EnforcedSettings,
  next: EnforcedSettings,
): EnforcedSettings {
  const merged: EnforcedSettings = { ...current, ...next };
  for (const key of NUMERIC_ENFORCED_SETTING_KEYS) {
    const strictest = minDefined(current[key], next[key]);
    if (strictest !== undefined) {
      merged[key] = strictest;
    }
  }
  const allowedItemUnits = intersectDefined(
    current.allowedItemUnits,
    next.allowedItemUnits,
  );
  if (allowedItemUnits) {
    merged.allowedItemUnits = allowedItemUnits;
  }
  return merged;
}

/**
 * Appends the provider to every setting it locks.
 * @param lockedBy - The providers recorded so far per setting
 * @param providerId - The provider being merged
 * @param settings - The settings the provider locks
 * @returns The updated providers per setting
 */
function mergeLockedBy(
  lockedBy: SettingsLockedBy,
  providerId: ERPProviderKey,
  settings: EnforcedSettings,
): SettingsLockedBy {
  const merged: SettingsLockedBy = { ...lockedBy };
  for (const key of ENFORCED_SETTING_KEYS) {
    if (settings[key] !== undefined) {
      merged[key] = [...(lockedBy[key] ?? []), providerId];
    }
  }
  return merged;
}

/**
 * Returns the smaller of two optional numbers.
 * @param a - The first number, if any
 * @param b - The second number, if any
 * @returns The minimum of the defined numbers, or undefined when neither is defined
 */
function minDefined(
  a: number | undefined,
  b: number | undefined,
): number | undefined {
  if (a === undefined) {
    return b;
  }
  if (b === undefined) {
    return a;
  }
  return Math.min(a, b);
}

/**
 * Returns the entries two optional lists share.
 * @param a - The first list, if any
 * @param b - The second list, if any
 * @returns The shared entries, the defined list when only one is defined, or undefined when neither is
 */
function intersectDefined<T>(
  a: T[] | undefined,
  b: T[] | undefined,
): T[] | undefined {
  if (a === undefined) {
    return b;
  }
  if (b === undefined) {
    return a;
  }
  return a.filter((entry) => b.includes(entry));
}
